import React, { useEffect, useState } from "react";
import { useExplainArticle, useNytPopular, useNytTopStories, useWorkspaceSearch } from "@/hooks/useNews";
import type { NewsRange, NewsWorkspaceItem } from "@/lib/types";

function ResultRow({ item, onSelect }: { item: NewsWorkspaceItem; onSelect: (item: NewsWorkspaceItem) => void }) {
  return (
    <button type="button" className="workspace-news-row" onClick={() => onSelect(item)}>
      <span>{item.title}</span><small>{item.provider} · {new Date(item.pubDate).toLocaleString()}</small>
    </button>
  );
}

function WorkspaceDetails({ item }: { item: NewsWorkspaceItem }) {
  const explain = useExplainArticle();
  useEffect(() => { explain.reset(); }, [item.sourceRef]);
  return (
    <section className="workspace-details" aria-label="Selected article details">
      <h2>{item.title}</h2>
      {item.description && <p>{item.description}</p>}
      {item.origin.startsWith("nyt-") && <p className="nyt-attribution">Data provided by The New York Times</p>}
      <div className="workspace-actions">
        <a href={item.link} target="_blank" rel="noopener noreferrer">Read full article at {item.provider}</a>
        <button type="button" onClick={() => explain.mutate(item)} disabled={explain.isPending}>EXPLAIN THIS</button>
      </div>
      {explain.data && (
        <div className="workspace-explanation">
          <strong>TSM heuristic interpretation</strong>
          <p>{explain.data.explanation.summary}</p>
          <dl>
            <dt>Why it matters</dt><dd>{explain.data.explanation.whyItMatters}</dd>
            <dt>Market angle</dt><dd>{explain.data.explanation.marketAngle}</dd>
            <dt>Affected assets mentioned</dt><dd>{explain.data.explanation.affectedAssets.join(", ") || "None explicitly named"}</dd>
            <dt>Confidence</dt><dd>{Math.round(explain.data.explanation.confidence * 100)}%</dd>
            <dt>Source basis</dt><dd>{explain.data.explanation.sourceBasis}</dd>
          </dl>
        </div>
      )}
      {explain.error && <p className="text-red-400">{explain.error.message}</p>}
    </section>
  );
}

export default function NewsWorkspacePanel({ mode }: { mode: "live" | "search" | "popular" }) {
  const [selected, setSelected] = useState<NewsWorkspaceItem | null>(null);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState<NewsRange>("30d");
  const [period, setPeriod] = useState<1 | 7 | 30>(1);
  const top = useNytTopStories(mode === "live");
  const popular = useNytPopular(period, mode === "popular");
  const search = useWorkspaceSearch();

  useEffect(() => { setSelected(null); }, [mode, period]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSelected(null);
    if (query.trim().length >= 2) search.mutate({ query: query.trim(), range });
  };

  if (mode === "live") {
    return (
      <section className="nyt-top-strip">
        <div className="terminal-section-header"><span>NYT Top Stories</span><small>Data provided by The New York Times · {top.data?.nyt.status ?? "loading"}</small></div>
        <div className="workspace-row-list">{top.data?.nyt.items.slice(0, 10).map((item) => <ResultRow key={item.sourceRef} item={item} onSelect={setSelected} />)}</div>
        {top.data?.nyt.message && <p className="workspace-empty">{top.data.nyt.message}</p>}
        {selected && <WorkspaceDetails item={selected} />}
      </section>
    );
  }

  if (mode === "popular") {
    return (
      <div className="workspace-mode">
        <div className="workspace-toolbar"><strong>NYT reader attention — not market impact</strong>{([1, 7, 30] as const).map((value) => <button key={value} onClick={() => setPeriod(value)} className={period === value ? "is-active" : ""}>{value} DAY{value > 1 ? "S" : ""}</button>)}</div>
        <p className="nyt-attribution workspace-attribution">Data provided by The New York Times · {popular.data?.nyt.status ?? "loading"}</p>
        <div className="workspace-row-list">{popular.data?.nyt.items.map((item) => <ResultRow key={item.sourceRef} item={item} onSelect={setSelected} />)}</div>
        {selected && <WorkspaceDetails item={selected} />}
      </div>
    );
  }

  return (
    <div className="workspace-mode">
      <form className="workspace-toolbar" onSubmit={submit}>
        <label>Search current news and NYT <input value={query} onChange={(e) => setQuery(e.target.value)} maxLength={120} /></label>
        <select value={range} onChange={(e) => setRange(e.target.value as NewsRange)}><option value="24h">24h</option><option value="7d">7d</option><option value="30d">30d</option><option value="1y">1y</option></select>
        <button type="submit" disabled={search.isPending}>SEARCH</button>
      </form>
      {search.data && <>
        <div className="terminal-section-header">CURRENT NEWS · {search.data.current.items.length}</div>
        <div className="workspace-row-list">{search.data.current.items.map((item) => <ResultRow key={item.sourceRef} item={item} onSelect={setSelected} />)}</div>
        <div className="terminal-section-header">NYT SEARCH · {search.data.nyt.items.length} · Data provided by The New York Times</div>
        <div className="workspace-row-list">{search.data.nyt.items.map((item) => <ResultRow key={item.sourceRef} item={item} onSelect={setSelected} />)}</div>
      </>}
      {search.error && <p className="text-red-400">{search.error.message}</p>}
      {selected && <WorkspaceDetails item={selected} />}
    </div>
  );
}
